import React from "react";
import { IoMdClose } from "react-icons/io";
import { Link } from "react-router-dom";
import { MdAdd, MdDelete } from "react-icons/md";

const emptyEntry = (id) => ({
  id,
  opp: "",
  beginDate: "",
  endDate: "",
  duration: "",
});

const emptyTerm = (id) => ({
  id,
  sem: "",
  calendar: [emptyEntry(1)],
});

export const defaultCalendarValues = {
  year: "",
  info: "Academic calendar",
  sortOrder: 1,
  status: "published",
  terms: [emptyTerm(1), { ...emptyTerm(2), sem: "Semester Two" }],
};

export function reindexTerms(terms) {
  return terms.map((term, termIndex) => ({
    ...term,
    id: termIndex + 1,
    calendar: (term.calendar || []).map((entry, entryIndex) => ({
      ...entry,
      id: entryIndex + 1,
    })),
  }));
}

function CalendarForm({
  title,
  cancelPath,
  values,
  errors,
  touched,
  isSubmitting,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
}) {
  const addTerm = () => {
    const nextId = values.terms.length + 1;
    setFieldValue("terms", [...values.terms, emptyTerm(nextId)]);
  };

  const removeTerm = (termIndex) => {
    if (values.terms.length <= 1) return;
    setFieldValue(
      "terms",
      reindexTerms(values.terms.filter((_, i) => i !== termIndex))
    );
  };

  const addEntry = (termIndex) => {
    const terms = [...values.terms];
    const nextId = terms[termIndex].calendar.length + 1;
    terms[termIndex] = {
      ...terms[termIndex],
      calendar: [...terms[termIndex].calendar, emptyEntry(nextId)],
    };
    setFieldValue("terms", terms);
  };

  const removeEntry = (termIndex, entryIndex) => {
    const terms = [...values.terms];
    if (terms[termIndex].calendar.length <= 1) return;
    terms[termIndex] = {
      ...terms[termIndex],
      calendar: terms[termIndex].calendar
        .filter((_, i) => i !== entryIndex)
        .map((entry, i) => ({ ...entry, id: i + 1 })),
    };
    setFieldValue("terms", terms);
  };

  const updateEntry = (termIndex, entryIndex, field, value) => {
    const terms = [...values.terms];
    terms[termIndex].calendar[entryIndex] = {
      ...terms[termIndex].calendar[entryIndex],
      [field]: value,
    };
    setFieldValue("terms", terms);
  };

  const updateTerm = (termIndex, field, value) => {
    const terms = [...values.terms];
    terms[termIndex] = { ...terms[termIndex], [field]: value };
    setFieldValue("terms", terms);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full flex justify-start flex-col items-start border shadow-sm rounded-xl"
    >
      <div className="flex justify-between items-center py-2.5 px-4 border-b w-full">
        <h3 className="font-bold text-gray-800">{title}</h3>
        <Link
          to={cancelPath}
          className="inline-flex flex-shrink-0 justify-center items-center h-8 w-8 rounded-md text-gray-500 hover:text-gray-400"
        >
          <IoMdClose />
        </Link>
      </div>

      <div className="p-4 w-full space-y-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Academic Year
            </label>
            <input
              placeholder="e.g. 2025 - 2026"
              name="year"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.year}
              className="placeholder:italic placeholder:text-slate-400 block bg-white w-full border border-slate-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-sky-500 sm:text-sm"
            />
            {errors.year && touched.year && (
              <p className="text-red-500 text-sm mt-1">{errors.year}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Info Label
            </label>
            <input
              placeholder="Academic calendar"
              name="info"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.info}
              className="placeholder:italic placeholder:text-slate-400 block bg-white w-full border border-slate-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-sky-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Order
            </label>
            <input
              type="number"
              min="1"
              name="sortOrder"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.sortOrder}
              className="block bg-white w-full border border-slate-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-sky-500 sm:text-sm"
            />
          </div>
        </div>

        {values.terms.map((term, termIndex) => (
          <div
            key={term.id}
            className="border rounded-lg p-4 bg-slate-50 space-y-4"
          >
            <div className="flex justify-between items-center gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Semester Name
                </label>
                <input
                  placeholder="e.g. Semester one"
                  value={term.sem}
                  onChange={(e) =>
                    updateTerm(termIndex, "sem", e.target.value)
                  }
                  className="block bg-white w-full border border-slate-300 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-sky-500 sm:text-sm"
                />
                {errors.terms?.[termIndex]?.sem && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.terms[termIndex].sem}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeTerm(termIndex)}
                className="mt-6 text-red-600 hover:text-red-800"
                title="Remove semester"
              >
                <MdDelete size={22} />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b">
                    <th className="py-2 pr-2">Operation</th>
                    <th className="py-2 pr-2">Beginning Date</th>
                    <th className="py-2 pr-2">Ending Date</th>
                    <th className="py-2 pr-2">Duration</th>
                    <th className="py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {term.calendar.map((entry, entryIndex) => (
                    <tr key={entry.id} className="border-b border-gray-200">
                      <td className="py-2 pr-2">
                        <input
                          value={entry.opp}
                          onChange={(e) =>
                            updateEntry(
                              termIndex,
                              entryIndex,
                              "opp",
                              e.target.value
                            )
                          }
                          placeholder="Classes start"
                          className="block bg-white w-full border border-slate-300 rounded-md py-1.5 px-2 text-sm"
                        />
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          value={entry.beginDate}
                          onChange={(e) =>
                            updateEntry(
                              termIndex,
                              entryIndex,
                              "beginDate",
                              e.target.value
                            )
                          }
                          placeholder="9 sept, 2025"
                          className="block bg-white w-full border border-slate-300 rounded-md py-1.5 px-2 text-sm"
                        />
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          value={entry.endDate}
                          onChange={(e) =>
                            updateEntry(
                              termIndex,
                              entryIndex,
                              "endDate",
                              e.target.value
                            )
                          }
                          placeholder="03 nov, 2025"
                          className="block bg-white w-full border border-slate-300 rounded-md py-1.5 px-2 text-sm"
                        />
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          value={entry.duration}
                          onChange={(e) =>
                            updateEntry(
                              termIndex,
                              entryIndex,
                              "duration",
                              e.target.value
                            )
                          }
                          placeholder="8 weeks"
                          className="block bg-white w-full border border-slate-300 rounded-md py-1.5 px-2 text-sm"
                        />
                      </td>
                      <td className="py-2">
                        <button
                          type="button"
                          onClick={() => removeEntry(termIndex, entryIndex)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <MdDelete size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              onClick={() => addEntry(termIndex)}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              <MdAdd /> Add row
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addTerm}
          className="flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium"
        >
          <MdAdd size={20} /> Add semester
        </button>

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-blue-500 text-[18px] font-medium text-white py-3 w-fit px-5 rounded-[3px] uppercase hover:bg-[#465B53] disabled:opacity-60"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </form>
  );
}

export default CalendarForm;
